import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon } from '@/components/icons';

import './lightboxPreview.css';

/**
 * LightboxShell — 全站燈箱的**外框單一來源**(R226 從 `LightboxPreview` 抽出)。
 * -----------------------------------------------------------------------------
 * 只負責四件事,不碰內容:
 *   1. 遮罩(native button — 照 `ResultLightbox` 先例過 a11y)
 *   2. `role="dialog" aria-modal` 的定位框(`.hai-lb`,幾何在 lightboxPreview.css)
 *   3. 頭列(左側 `head` 插槽 + 右側關閉鈕)
 *   4. **三種關閉**(ESC / 遮罩 / 鈕)+ 焦點管理(開啟 focus 關閉鈕、卸載還給 opener)
 *
 * ## 為什麼要抽(CIS §05「禁止做第二套版型」)
 * R226 要把檢查報告從「整頁 route」改成「素材庫上的燈箱」(D3 `library.js:112`
 * `openRptLb` 才是真實流程)。報告的內容結構與 `LightboxPreview` 的
 * `media | copyReadonly` union 完全不同,硬塞進那個 union 會長出第三套 —— 而
 * [OVERVIEW §7](../../../docs/OVERVIEW.md) 已經記著「燈箱兩份實作待收斂」這筆債。
 * 所以改成**共用外框 + 各自內容**:焦點/ESC/遮罩/z-index 只有一份實作,
 * 設計端改 CIS 燈箱幾何時只有一個地方要改。
 *
 * ## size
 * - `default` — v1.6 素材預覽(880px / 88vh)
 * - `report`  — 檢查報告(D3 `.lb-rpt`:1240px / 92vh **固定高**)。固定高是刻意的:
 *   報告內有 sticky 的分類導覽列與左側 rail,容器高度會變的話 sticky 的參考框
 *   跟著跳。
 * - `wizard`  — 品牌規範精靈(D3 `.wiz-shell`:900px,**高度隨內容**)。
 *   ⚠️ 它跟 `report` 的差別不只是窄:`report` 那個 **`height: 92vh` 固定高**對
 *   精靈是災難 —— 內容只有半頁時,下半整片空白、動作列被推到離內容 300px 遠。
 *   R247.4 之前精靈就是借用 `report`,「看起來很空」七成出在這裡。
 * - `narrow`  — 素材成效詳情(D3 成效報表.html `.lb`:560px,高度隨內容)。
 *   指標卡是 2 欄小卡,借 880px 的 default 會讓每張卡寬到填充率只剩三成
 *   (R247.4 精靈借 report 的同型問題)。
 */
export type LightboxSize = 'default' | 'report' | 'wizard' | 'narrow';

const SIZE_CLASS: Record<LightboxSize, string> = {
  default: 'hai-lb',
  report: 'hai-lb hai-lb-rpt',
  wizard: 'hai-lb hai-lb-wiz',
  narrow: 'hai-lb hai-lb-nrw',
};

export function LightboxShell({
  ariaLabel,
  head,
  closeTitle,
  onClose,
  size = 'default',
  backdropClose = true,
  bodyClassName,
  bodyStyle: bodyStyleProp,
  footer,
  children,
  'data-testid': testId,
}: {
  /** 對話框的 accessible name(讀給螢幕閱讀器,不一定等於畫面上的標題)。 */
  ariaLabel: string;
  /** 頭列左側內容(標題 / chips / 徽章;關閉鈕由 shell 自己畫在最右)。 */
  head?: ReactNode;
  closeTitle: string;
  onClose: () => void;
  size?: LightboxSize;
  /**
   * `false` = 遮罩點擊與 ESC 都不關,只剩右上 X(FB-20260828-0001:品牌規範精靈
   * 這類「使用者輸入了一堆東西」的燈箱,誤觸陰暗區 = 整包 draft 蒸發)。
   * 預設 `true` —— 純預覽/唯讀燈箱維持三種關閉出口,不改全站行為。
   */
  backdropClose?: boolean;
  bodyClassName?: string;
  bodyStyle?: CSSProperties;
  footer?: ReactNode;
  children: ReactNode;
  'data-testid'?: string;
}) {
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<Element | null>(null);
  // R393(T11 RC3)— ESC 也要呼叫**最新的** onClose(mount-once 的閉包會凍結掛載當下那支)。
  // 需求描述放大燈箱的 onClose = 落盤 + 關窗:凍結的那支讀到的是掛載當下的 brief(空的),
  // 按 ESC 等於沒落盤 → 打的字只活在草稿裡,焦點又還給放大鈕、之後沒有 blur 可以補存。
  // R393(T3)— ESC 走分層(`escapeLayer`):燈箱上再開一層(確認框 / 下拉)時,ESC 只關最上層。
  // `backdropClose=false` 的燈箱照樣佔著那一層(ESC 不可以穿過它去關底下那層)。
  useEscapeLayer(onClose, true, backdropClose);

  useEffect(() => {
    openerRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
    // mount-once 語意(開/關由條件渲染控制)。
  }, []);

  return (
    <>
      {/* 遮罩 = native button(ESC / 關閉鈕是另兩個出口);backdropClose=false 時
          遮罩降為純視覺(輸入型燈箱的誤觸防護,FB-20260828-0001) */}
      <button
        type="button"
        className="hai-lb-scrim"
        aria-label={closeTitle}
        onClick={backdropClose ? onClose : undefined}
        style={scrimBtnStyle}
        data-testid="lightbox-scrim"
      />
      <div
        className={SIZE_CLASS[size]}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        data-testid={testId ?? 'lightbox-preview'}
      >
        <div style={headStyle}>
          {head}
          <button
            ref={closeBtnRef}
            type="button"
            className="hai-lb-close"
            onClick={onClose}
            title={closeTitle}
            aria-label={closeTitle}
            style={closeBtnStyle}
            data-testid="lightbox-close"
          >
            <HaiIcon name="x" />
          </button>
        </div>
        <div
          className={bodyClassName ?? ''}
          style={{ ...bodyStyle, ...(bodyStyleProp ?? {}) }}
          data-testid="lightbox-body"
        >
          {children}
        </div>
        {footer && <div style={footStyle}>{footer}</div>}
      </div>
    </>
  );
}

const scrimBtnStyle: CSSProperties = { border: 'none', padding: 0, cursor: 'default' };

const headStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '14px 18px',
  borderBottom: '1px solid var(--slate-100)',
  flexShrink: 0,
};

const closeBtnStyle: CSSProperties = {
  marginLeft: 'auto',
  width: 32,
  height: 32,
  borderRadius: 9,
  border: 'none',
  background: 'none',
  color: 'var(--ink-600)',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  flex: '0 0 32px',
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: 20,
};

const footStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '14px 18px',
  borderTop: '1px solid var(--slate-100)',
  flexShrink: 0,
};
